import React, { useContext, useEffect, useRef, useState } from 'react';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import { ApiContext } from '@api/api';
import { NO_LABEL_FILTER_VALUE } from '@components/framework/tags/LabelsTag.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import { Button } from 'primereact/button';
import {
  AllReservationsFilter,
  CodeTypeEnum,
  CompanyModuleEnum,
  EntityTypeEnum,
  FeaturesEnum,
  MassActionReservationRejectedDTO,
  MassActionReservationsOutDTO,
  Pageable,
  PermissionTypesEnum,
  ReservationActionTypeEnum,
  ReservationFlagTypeEnum,
  ReservationListPreviewOutDTO,
  ReservationRejectionReasonEnum,
  TableKeyEnum,
} from '@api/ringil-api.v2';
import { isNotBlank, isNotNullOrUndefined, isNullOrUndefined, useLocalStorage, visualiseWorkflowStateState } from '@utils/utils';
import { dayjsFromApiPrague, dayjsFromApiUtcInTz, getBrowserTimezone, stringHHmmFromStringInTz, stringYMDFromStringInTz } from '@utils/dateWithTz';
import useTranslationLgs from '../../../hooks/i18n/useTranslation';
import dayjs from 'dayjs';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import { useCreateEditReservationSlotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/EditReservationSlot';
import LocationTargetSvg from '@icons/LocationTargetSvg.tsx';
import CalendarSvg from '@icons/calendar.svg?react';
import ClockSvg from '@icons/ClockSvg.tsx';
import BuildingSvg from '@icons/BuildingSvg.tsx';
import TruckSvg from '@icons/TruckSvg.tsx';
import useWorkflowStates from '@hooks/useWorkFlowStates/useWorkflowStates.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import { flagUnitsText } from '@components/RingilComponents/form/flagUnitsLimit';
import SvgChevron from '@icons/chevron.svg?react';
import SvgLink from '@icons/link.svg?react';
import SvgOrder from '@icons/order.svg?react';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import RingilDataTable, { ColumnConfig, DateTimeFormatType, FilterConfig, FilterType, OptionType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { MassActionConfig } from '@components/RingilDataTable/RingilMassActions.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import RingilDataTableV3, { TableViewsConfig } from '@components/RingilDataTable/v3/RingilDataTableV3.tsx';
import LegacyVersionSwitch from '@components/framework/LegacyVersionSwitch.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import ReservationDetail from '@components/ringil3/Features/Reservation/ReservationDetail';
import { buildReservationFields } from './v3/reservationFields';
import { RESERVATION_FIELD_GROUPS, RESERVATION_PRESETS, reservationDefaultColumns } from './v3/reservationFieldKeys';

// Mirror MAX_RESERVATION_MASS_ARCHIVE_SIZE / MAX_RESERVATION_MASS_DELETE_SIZE on the backend.
const MAX_RESERVATION_MASS_ARCHIVE = 50;
const MAX_RESERVATION_MASS_DELETE = 50;

const REJECTION_REASON_FALLBACK: Record<ReservationRejectionReasonEnum, string> = {
  [ReservationRejectionReasonEnum.NOT_FOUND]: 'Not found',
  [ReservationRejectionReasonEnum.ALREADY_ARCHIVED]: 'Already archived',
  [ReservationRejectionReasonEnum.EDIT_BLOCKED_BEFORE_START]: 'Too close to the start time',
  [ReservationRejectionReasonEnum.DELETE_NOT_ALLOWED]: 'Deleting is not allowed',
};

type MassActionKind = 'archive' | 'delete';

interface ExtendedReservationListPreviewOutDTO extends ReservationListPreviewOutDTO {
  isAttachmentExpanded?: boolean;
  locationId?: string;
  isPinnedOnLocRampTable?: boolean;
  driverInfo?: {
    driverPhone?: string;
    driverName?: string;
  };
  hasTable?: boolean;
  linkedToExpCoords?: {
    expeditionId?: string;
  };
}

// Column ordering/metadata only — the render function is attached fresh on every
// render so its closure always captures the current tag/label expansion state.
interface ColumnMeta {
  id: string;
  field?: string;
  header?: string;
  headerDefault?: string;
  width?: string;
  sortable?: boolean;
  isDefaultSort?: boolean;
  order?: number;
}

const getDefaultColumnMeta = (): ColumnMeta[] => [
  { id: 'applicationId', field: 'applicationId', header: 'ID', headerDefault: 'ID', width: '20%', sortable: true, isDefaultSort: true, order: 1 },
  { id: 'timeslotAndRamp', field: 'timeslot.since', header: 'Rampa a čas', headerDefault: 'Ramp and time', width: '20%', sortable: true, order: 2 },
  { id: 'customerAndCarrier', field: 'customerName', header: 'Zákazník a dopravce', headerDefault: 'Customer and carrier', width: '20%', order: 3 },
  { id: 'reference', field: 'reservationName', header: 'Reference', headerDefault: 'Reference', width: '20%', order: 4 },
  { id: 'manipulationUnits', field: 'orders', header: 'Další informace', headerDefault: 'Additional information', width: '20%', order: 5 },
];

export const ResponsiveReservationList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const {
    getReservations,
    downloadAttachment,
    getColumnOrderSetting,
    getUserReservationFilters,
    saveUserReservationFilters,
    deleteUserFilter,
    listAllLabelsForCompany,
    lovCompanyCodeList,
    lovInheritorsIncludingSelf,
    lovOwnersIncludingSelf,
    massArchiveReservations,
    massDeleteReservations,
  } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);

  const reservationExportFlag = useSecuredFlag('reservation-export', {
    kind: 'action',
    description: 'Export reservations (generic export API)',
    features: [FeaturesEnum.GENERIC_EXPORT_API],
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_EXPORT],
  });

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const timeslotListRwFlag = useSecuredFlag('timeslot-list-rw', {
    kind: 'action',
    description: 'Create / manage reservations in the list',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_RW],
  });
  const timeslotsModuleFlag = useSecuredFlag('module-timeslots-owned', {
    kind: 'feature',
    description: 'Company owns the Timeslots module',
    modules: [{ module: CompanyModuleEnum.TIMESLOTS, inherited: false }],
  });
  const hasOwnedTimeslotModule = timeslotsModuleFlag.granted;
  // RD-2977: folder filter - the LOV already returns only the folders the user may access
  const reservationFolders = useFolders(EntityTypeEnum.TIMESLOT);

  const massArchiveFlag = useSecuredFlag('reservation-mass-archive', {
    kind: 'action',
    description: 'Archive selected reservations',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DETAIL_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_RW],
  });
  const massDeleteFlag = useSecuredFlag('reservation-mass-delete', {
    kind: 'action',
    description: 'Delete selected reservations',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DELETE],
  });

  const onBehalfOfFeatureFlag = useSecuredFlag('reservation-on-behalf-of', {
    kind: 'feature',
    description: 'Reservation on behalf of',
    features: [FeaturesEnum.RESERVATION_ON_BEHALF_OF],
  });
  const hasOnBehalfOf = onBehalfOfFeatureFlag.granted;

  const [revealTabTexts, setRevealTabTexts] = useLocalStorage<boolean>('ringil_tags_expanded', false);
  const [expandedFlagTags, setExpandedFlagTags] = useLocalStorage<Record<string, boolean>>('ringil_reservation_flag_tags_expanded', {});

  const toggleFlagTag = (key: ReservationFlagTypeEnum | 'attachments' | 'orders') => {
    setExpandedFlagTags(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const setAllFlagTagsExpanded = (expanded: boolean) => {
    setExpandedFlagTags(_ => {
      const next: Record<string, boolean> = { all: expanded, attachments: expanded, orders: expanded };
      Object.values(ReservationFlagTypeEnum).forEach(t => {
        next[t] = expanded;
      });
      return next;
    });
  };

  const { CreateTimeslotSidebar, createTimeslotRequest, setCreateTimeslotRequest } = useCreateTimeslotSidebar();
  const [editReservation, setEditReservation] = useState<ExtendedReservationListPreviewOutDTO | null>(null);
  const { EditReservationSlotSidebar, setVisible: setEditSidebarVisible, visible: editSidebarVisible } = useCreateEditReservationSlotSidebar();

  // Load workflow states for TIMESLOT entity type
  const timeslotStates = useWorkflowStates(EntityTypeEnum.TIMESLOT);

  // Label options for the labels filter (transformed from the old InputLabel into a MultiSelect)
  const [labelOptions, setLabelOptions] = useState<OptionType[]>([]);
  useEffect(() => {
    listAllLabelsForCompany(d => {
      const labels = d?.find(t => t.entityType === EntityTypeEnum.TIMESLOT)?.labels ?? [];
      setLabelOptions([
        { label: tr('generic.withoutLabel', 'Without label'), value: NO_LABEL_FILTER_VALUE, active: true },
        ...labels.map(l => ({
          label: l.labelValue,
          value: l.labelId,
          active: true,
          icon: <span className="inline-block w-[14px] h-[14px] rounded-sm" style={{ backgroundColor: l.labelColor }} />,
        })),
      ]);
    });
  }, []);

  // Carrier/customer company options for the filters (timeslots inheritance based)
  const [carrierOptions, setCarrierOptions] = useState<OptionType[]>([]);
  const [customerOptions, setCustomerOptions] = useState<OptionType[]>([]);
  useEffect(() => {
    // carrier = companies that inherit my TIMESLOTS module (+ me if I inherit too)
    lovInheritorsIncludingSelf(CompanyModuleEnum.TIMESLOTS, d =>
      setCarrierOptions((d ?? []).map(c => ({ label: c.name, value: c.id, active: true }))),
    );
    // customer = companies whose TIMESLOTS I inherit (+ me if I own too)
    lovOwnersIncludingSelf(CompanyModuleEnum.TIMESLOTS, d =>
      setCustomerOptions((d ?? []).map(c => ({ label: c.name, value: c.id, active: true }))),
    );
  }, []);

  // On-behalf-of options for the filter (only when the feature is enabled)
  const [onBehalfOfOptions, setOnBehalfOfOptions] = useState<OptionType[]>([]);
  useEffect(() => {
    if (!hasOnBehalfOf) {
      setOnBehalfOfOptions([]);
      return;
    }
    lovCompanyCodeList(CodeTypeEnum.ON_BEHALF_OF, '', d => {
      setOnBehalfOfOptions(
        (d ?? []).map(o => ({
          label: o.name ?? o.externalId,
          value: o.id,
          active: true,
        })),
      );
    });
  }, [hasOnBehalfOf]);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ExtendedReservationListPreviewOutDTO[]; clear: () => void }>(null);

  const [massActionBusy, setMassActionBusy] = useState(false);
  const [pendingMassDelete, setPendingMassDelete] = useState<ExtendedReservationListPreviewOutDTO[]>([]);

  const massActionHeading = (kind: MassActionKind) =>
    kind === 'archive' ? tr('reservations.massArchiveResultHeading', 'Archived') : tr('reservations.massDeleteResultHeading', 'Deleted');

  const massActionSummary = (kind: MassActionKind, processed: number, skipped: number) =>
    kind === 'archive'
      ? tr('reservations.massArchiveSummary', '{{processed}} archived, {{skipped}} skipped.', { processed, skipped })
      : tr('reservations.massDeleteSummary', '{{processed}} deleted, {{skipped}} skipped.', { processed, skipped });

  const summarizeRejections = (rejected: MassActionReservationRejectedDTO[]): string =>
    Object.entries(
      rejected.reduce<Record<string, number>>((acc, item) => {
        const label = tr(`reservations.massActionReason.${item.reservationRejectionReason}`, REJECTION_REASON_FALLBACK[item.reservationRejectionReason]);
        acc[label] = (acc[label] ?? 0) + 1;
        return acc;
      }, {}),
    )
      .map(([label, count]) => `${count} ${label}`)
      .join(', ');

  const onMassActionDone = (kind: MassActionKind) => (data: MassActionReservationsOutDTO) => {
    setMassActionBusy(false);
    setPendingMassDelete([]);
    tableRef.current?.clear();
    tableRef.current?.reloadData();
    const processed = data?.processedIds?.length ?? 0;
    const rejected = data?.rejected ?? [];
    const summary = massActionSummary(kind, processed, rejected.length);
    showToastMessage(massActionHeading(kind), rejected.length ? `${summary} (${summarizeRejections(rejected)})` : summary, rejected.length ? 'warn' : 'success');
  };

  const massActionCallbacks = {
    successToast: false,
    onError: (_error: unknown, showStandardError: () => void) => {
      showStandardError();
      setMassActionBusy(false);
      setPendingMassDelete([]);
    },
    onValidationFailed: (errors: { message?: string }[]) => {
      setMassActionBusy(false);
      setPendingMassDelete([]);
      showToastMessage(
        tr('generic.error', 'Error'),
        (errors ?? []).map((error, index) => <div key={index}>{error.message}</div>),
        'error',
      );
    },
  };

  const runMassArchive = (rows: ExtendedReservationListPreviewOutDTO[]) => {
    if (rows.length === 0 || massActionBusy) return;
    setMassActionBusy(true);
    massArchiveReservations({ reservationIds: rows.map(row => row.id) }, onMassActionDone('archive'), null, massActionCallbacks);
  };

  const confirmMassDelete = () => {
    if (pendingMassDelete.length === 0 || massActionBusy) return;
    setMassActionBusy(true);
    massDeleteReservations({ reservationIds: pendingMassDelete.map(row => row.id) }, onMassActionDone('delete'), null, massActionCallbacks);
  };

  const tooManySelectedReason = (max: number) => (selected: ExtendedReservationListPreviewOutDTO[]) =>
    selected.length > max
      ? tr('reservations.massActionTooMany', 'At most {{max}} reservations can be processed at once; {{count}} are selected.', { max, count: selected.length })
      : undefined;

  const massActionConfig: MassActionConfig<ExtendedReservationListPreviewOutDTO>[] = [
    {
      label: tr('ReservationMoreButtons.archiveReservation', 'Archive'),
      onClick: () => runMassArchive(tableRef.current?.selectedObjects ?? []),
      disabled: selected => selected.length === 0 || selected.length > MAX_RESERVATION_MASS_ARCHIVE || massActionBusy,
      disabledReason: tooManySelectedReason(MAX_RESERVATION_MASS_ARCHIVE),
      secured: () => ({ id: 'reservation-mass-archive', label: 'Archive selected reservations', description: 'Archive the reservations selected in the list', visible: massArchiveFlag }),
    },
    {
      label: tr('generic.delete', 'Delete'),
      onClick: () => setPendingMassDelete(tableRef.current?.selectedObjects ?? []),
      disabled: selected => selected.length === 0 || selected.length > MAX_RESERVATION_MASS_DELETE || massActionBusy,
      disabledReason: tooManySelectedReason(MAX_RESERVATION_MASS_DELETE),
      secured: () => ({ id: 'reservation-mass-delete', label: 'Delete selected reservations', description: 'Delete the reservations selected in the list', visible: massDeleteFlag }),
    },
  ];

  const normalizeReservationFilter = (value?: AllReservationsFilter): AllReservationsFilter => {
    return {
      ...(value ?? {}),
      userTimezoneId: getBrowserTimezone(),
    } as AllReservationsFilter;
  };

  const handleFileClick = (attachmentId: string, fileName: string) => {
    downloadAttachment(attachmentId, data => {
      // @ts-ignore
      const linkSource = `data:${data.mimeType};base64,${data.data}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    });
  };

  const truncateFileName = (fileName: string, maxLength: number = 10): string => {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.slice(0, -(extension.length + 1));

    if (nameWithoutExt.length <= maxLength) return fileName;

    const halfLength = Math.floor((maxLength - 3) / 2);
    const start = nameWithoutExt.slice(0, halfLength);
    const end = nameWithoutExt.slice(-halfLength);

    return `${start}...${end}.${extension}`;
  };

  const hasReservation = (data: ExtendedReservationListPreviewOutDTO): boolean => {
    if (isNullOrUndefined(data?.timeslot)) return false;
    if (isNullOrUndefined(data?.timeslot.since) && isNotNullOrUndefined(data?.timeslot.utcStart)) return false;
    if (isNullOrUndefined(data?.timeslot.till) && isNotNullOrUndefined(data?.timeslot.utcEnd)) return false;
    if (isNotBlank(data?.timeslot.utcStart) && isNullOrUndefined(data?.timeslot.timezoneId)) return false;

    return true;
  };

  const renderTimeslotAndRamp = (data: ExtendedReservationListPreviewOutDTO) => {
    const isMigrated = isNotNullOrUndefined(data.timeslot?.timezoneId) && isNotNullOrUndefined(data.timeslot?.utcStart) && isNotNullOrUndefined(data.timeslot?.utcEnd);

    const timeslotDate = isMigrated
      ? stringYMDFromStringInTz(data.timeslot.utcStart, data.timeslot.timezoneId)
      : stringYMDFromStringInTz(data.timeslot?.since, 'Europe/Prague');
    const since = isMigrated
      ? stringHHmmFromStringInTz(data.timeslot.utcStart, data.timeslot.timezoneId)
      : stringHHmmFromStringInTz(data.timeslot?.since, 'Europe/Prague');
    const till = isMigrated ? stringHHmmFromStringInTz(data.timeslot.utcEnd, data.timeslot.timezoneId) : stringHHmmFromStringInTz(data.timeslot?.till, 'Europe/Prague');

    return (
      <div className="flex flex-col">
        <div className="font-medium flex items-center gap-2" data-testid={`reservations-row-${data.applicationId}-ramp`}>
          <LocationTargetSvg />
          {data.rampName}
        </div>
        <SecuredArea
          id="row-timeslot" instance={data.applicationId}
          visible={[{ type: 'data', label: 'hasReservation(data)', satisfied: hasReservation(data) }]}
          label="Reservation timeslot block"
        >
          <div className="mt-1">
            <div className="flex items-center gap-2" data-testid={`reservations-row-${data.applicationId}-timeslotDate`}>
              <CalendarSvg className={'text-content-secondary'} />
              {timeslotDate}
            </div>
            <div className="flex items-center gap-2 text-gray-600" data-testid={`reservations-row-${data.applicationId}-timeslotTimeRange`}>
              <ClockSvg />
              {since} - {till}
            </div>
            <COTimezone dataTestId={`reservations-row-${data.applicationId}-timezone`} timezoneId={data.timeslot?.timezoneId} />
          </div>
        </SecuredArea>
      </div>
    );
  };

  const renderCustomerAndCarrier = (data: ExtendedReservationListPreviewOutDTO) => (
    <div className="flex flex-col">
      <div className="font-medium flex items-center gap-2" data-testid={`reservations-row-${data.applicationId}-customerName`}>
        <BuildingSvg />
        {data.customerName}
      </div>
      <div className="text-gray-600 flex items-center gap-2" data-testid={`reservations-row-${data.applicationId}-carrierName`}>
        <TruckSvg />
        {data.carrierName}
      </div>
      <SecuredArea
        id="row-driver" instance={data.applicationId}
        visible={[{ type: 'data', label: 'driverName || carPlate present', satisfied: !!(data.driverInfo?.driverName || data.carPlate) }]}
        label="Driver / car plate"
      >
        <div className="text-gray-600" data-testid={`reservations-row-${data.applicationId}-carPlate`}>
          {[data.driverInfo?.driverName, data.carPlate].filter(t => t != null).join(', ')}
        </div>
      </SecuredArea>
    </div>
  );

  const renderReference = (data: ExtendedReservationListPreviewOutDTO) => (
    <div className="flex flex-col">
      <div className="font-medium" data-testid={`reservations-row-${data.applicationId}-reservationName`}>
        {data.reservationName}
      </div>
      <div className="text-gray-600" data-testid={`reservations-row-${data.applicationId}-orderName`}>
        {data.orders?.map((t: any) => t.orderName).join(', ')}
      </div>
      <SecuredArea
        id="row-preferred-date" instance={data.applicationId}
        visible={[{ type: 'data', label: 'preferredDate present', satisfied: !!data.preferredDate }]}
        label="Preferred date"
      >
        <div>{stringYMDFromStringInTz(data.preferredDate, data.timeslot?.timezoneId || data.timezoneId)}</div>
      </SecuredArea>
    </div>
  );

  const renderManipulationUnits = (data: ExtendedReservationListPreviewOutDTO) => {
    const orderCount = data.orders?.length || 0;
    // Order tag shows only the manipulation units (aggregated cargo), not the order name/reference
    const manipulationUnits = (data.orders ?? [])
      .flatMap(order => order.items ?? [])
      .reduce((acc, val) => {
        const idx = acc.findIndex(t => t.cargoTemplate.code === val.cargoTemplate.code);
        if (idx === -1) {
          acc.push({ cargoTemplate: val.cargoTemplate, amount: val.amount });
        } else {
          acc[idx].amount += val.amount;
        }
        return acc;
      }, [] as { cargoTemplate: { code: string; name: string }; amount: number }[])
      .map(t => `${t.amount}x ${t.cargoTemplate.name}`)
      .join(', ');

    const hasManipulationUnits = isNotBlank(manipulationUnits);
    const hasFlags = !!data?.flags && data.flags.length > 0;
    const hasAttachments = !!data?.attachmentInfo && data.attachmentInfo.attachmentSize > 0;
    const hasAdditionalInfo = hasManipulationUnits || hasFlags || hasAttachments;

    return (
      <div className="flex flex-col">
        <div className="flex gap-2 flex-wrap items-start">
          <SecuredArea
            id="row-tag-all" instance={data.applicationId}
            visible={[{ type: 'data', label: 'any additional info available', satisfied: hasAdditionalInfo }]}
            label="Expand-all tag"
          >
            <ExpandableInfoAllTag
              id={`flags_all_${data.id}`}
              dataTestId={`reservations-row-${data.applicationId}-flags-all`}
              onClick={() => setAllFlagTagsExpanded(!expandedFlagTags?.['all'])}
              tooltipText={expandedFlagTags?.['all'] ? tr('expandableInfoTag.collapseAll', 'Collapse all') : tr('expandableInfoTag.expandAll', 'Expand all')}
              icon={<SvgChevron className={clsx('text-content-brand', expandedFlagTags?.['all'] && 'rotate-180')} />}
            />
          </SecuredArea>
          <SecuredArea id="row-flags" instance={data.id} visible={[{ type: 'data', label: 'flags.length > 0', satisfied: hasFlags }]} label="Reservation flags">
            {sortFlags(data.flags ?? []).map(f => (
              <div key={f.reservationFlagType} data-testid={`reservations-row-${data.applicationId}-flag-${f.reservationFlagType}`}>
                <ExpandableInfoTag
                  id={`flag_${f.reservationFlagType}_${data.id}`}
                  expanded={!!expandedFlagTags?.[f.reservationFlagType]}
                  onClick={() => toggleFlagTag(f.reservationFlagType)}
                  label={tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}
                  text={flagUnitsText(f) != null ? tr('CalendarEventCardDetail.flagUnits', ' ({{units}})', { units: flagUnitsText(f) }) : undefined}
                  icon={<FlagIcon type={f.reservationFlagType} className="w-3 h-3 m-1" />}
                />
              </div>
            ))}
          </SecuredArea>
          <SecuredArea id="row-orders" instance={data.id} visible={[{ type: 'data', label: 'manipulation units present', satisfied: hasManipulationUnits }]} label="Orders tag">
            <ExpandableInfoTag
              id={`orders_${data.id}`}
              dataTestId={`reservations-row-${data.applicationId}-orders`}
              expanded={!!expandedFlagTags?.['orders']}
              onClick={() => toggleFlagTag('orders')}
              label={tr('reservations.ordersTagLabel', 'Orders: ')}
              text={manipulationUnits}
              icon={<SvgOrder />}
              count={orderCount}
            />
          </SecuredArea>
          <SecuredArea id="row-attachments" instance={data.id} visible={[{ type: 'data', label: 'attachmentSize > 0', satisfied: hasAttachments }]} label="Attachments tag">
            <ExpandableInfoTag
              id={`attachments_${data.id}`}
              dataTestId={`reservations-row-${data.applicationId}-attachments`}
              expanded={!!expandedFlagTags?.['attachments']}
              onClick={() => toggleFlagTag('attachments')}
              icon={<SvgLink className={expandedFlagTags?.['attachments'] ? 'text-content-brand' : ''} />}
              label={tr('reservations.attachments', 'Attachments: ')}
              count={data.attachmentInfo?.attachmentSize ?? 0}
              text={data.attachmentInfo?.attachments?.map(a => truncateFileName(a.name)).join(' | ')}
              textComponent={
                <span className="font-medium">
                  {data.attachmentInfo?.attachments?.map((attachment, idx) => (
                    <React.Fragment key={attachment.id}>
                      <AttachmentLink id={attachment.id} name={attachment.name} onActivate={(id, fileName) => handleFileClick(id, fileName)} maxLength={15} />
                      {idx < data.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{' | '}</span>}
                    </React.Fragment>
                  ))}
                </span>
              }
            />
          </SecuredArea>
        </div>
      </div>
    );
  };

  const renderApplicationId = (data: ExtendedReservationListPreviewOutDTO) => (
    <>
      <div className="flex items-center gap-2">
        <Link to={navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(data.id))}>
          <div className="flex gap-2 font-['DM_Sans']">
            <span className={'text-blue whitespace-nowrap'} style={{ cursor: 'pointer' }} data-testid={`reservations-row-${data.applicationId}-applicationId`}>
              {data.applicationId}
              {data.hasETransportRO ? <>(E)</> : <></>}
            </span>
            <SecuredArea
              id="row-workflow-state" instance={data.applicationId}
              visible={[{ type: 'data', label: 'reservation status available', satisfied: !!data?.state }]}
              label="Workflow state badge"
            >
              <span>{visualiseWorkflowStateState(data?.state, tr, false)}</span>
            </SecuredArea>
          </div>
          <SecuredArea
            id="row-on-behalf-of" instance={data.applicationId}
            visible={[{ type: 'data', label: 'onBehalfOf present', satisfied: !!data.onBehalfOf }]}
            label="On behalf of"
          >
            <div className="text-gray-600 font-medium" data-testid={`reservations-row-${data.applicationId}-onBehalfOf`}>
              {data.onBehalfOf?.name ?? data.onBehalfOf?.externalId}
            </div>
          </SecuredArea>
          <div className="text-gray-600" data-testid={`reservations-row-${data.applicationId}-locationName`}>
            {data.locationName}
          </div>
        </Link>
      </div>

      <SecuredArea
        id="row-labels" instance={data.applicationId}
        visible={[
          {
            type: 'ownership',
            label: 'logged-in company owns reservation',
            satisfied: !!loggedUser && loggedUser.companyId === data.customerId,
            description: 'Owner only: labels belong to the company that owns the reservation',
            byDesign: true,
          },
          { type: 'data', label: 'labels.length > 0', satisfied: !!data.labels && data.labels.length > 0 },
        ]}
        label="Reservation labels"
        description="The reservation owner's labels on the reservation"
      >
        <ExpandableLabelWrapper labels={data.labels} isExpanded={revealTabTexts} onToggle={() => setRevealTabTexts(v => !v)} />
      </SecuredArea>
    </>
  );

  // Resolve a column's render function — by id first, then by header/field for
  // backend-defined columns. Looked up on every render so render closures always
  // capture the current tag/label expansion state.
  const getColumnRenderer = (col: ColumnMeta): ((row: ExtendedReservationListPreviewOutDTO) => React.ReactNode) => {
    switch (col.id) {
      case 'applicationId':
        return renderApplicationId;
      case 'timeslotAndRamp':
        return renderTimeslotAndRamp;
      case 'customerAndCarrier':
        return renderCustomerAndCarrier;
      case 'reference':
        return renderReference;
      case 'manipulationUnits':
        return renderManipulationUnits;
    }

    const headerMatch = col.header?.toLowerCase();
    const defaultHeaderMatch = col.headerDefault?.toLowerCase();
    const fieldMatch = col.field?.toLowerCase();

    if (headerMatch?.includes('id') || defaultHeaderMatch?.includes('id') || fieldMatch?.includes('id')) return renderApplicationId;
    if (headerMatch?.includes('rampa') || defaultHeaderMatch?.includes('ramp') || fieldMatch?.includes('timeslot')) return renderTimeslotAndRamp;
    if (headerMatch?.includes('zákazník') || defaultHeaderMatch?.includes('customer') || fieldMatch?.includes('customer')) return renderCustomerAndCarrier;
    if (headerMatch?.includes('reference') || defaultHeaderMatch?.includes('reference') || fieldMatch?.includes('reference')) return renderReference;
    if (headerMatch?.includes('další') || defaultHeaderMatch?.includes('additional') || fieldMatch?.includes('orders')) return renderManipulationUnits;
    return () => <div>-</div>;
  };

  // Backend-driven column ordering/metadata (defaults to the static set until loaded)
  const [columnMeta, setColumnMeta] = useState<ColumnMeta[]>(() => getDefaultColumnMeta());
  const [columnsLoaded, setColumnsLoaded] = useState<boolean>(false);

  // Load column configuration when the component mounts or when the user changes
  useEffect(() => {
    if (loggedUser?.companyId && !columnsLoaded) {
      fetchColumnConfiguration();
    }
  }, [loggedUser?.companyId, columnsLoaded]);

  const fetchColumnConfiguration = () => {
    if (!loggedUser?.companyId) return;

    getColumnOrderSetting(loggedUser.companyId, EntityTypeEnum.TIMESLOT, response => {
      if (!response.columnOrders || response.columnOrders.length === 0) {
        setColumnMeta(getDefaultColumnMeta());
      } else {
        const defaultMeta = getDefaultColumnMeta();

        // Map BE columns, reusing default metadata (id/width/sortable) when the header matches
        const mappedColumns: ColumnMeta[] = response.columnOrders.map(col => {
          const defaultColumn = defaultMeta.find(dc => dc.header === col.header || dc.headerDefault === col.header || dc.headerDefault === col.headerDefault);

          if (defaultColumn) {
            return {
              ...defaultColumn,
              header: col.header,
              headerDefault: col.headerDefault,
              order: col.order || 0,
            };
          }

          return {
            id: col.field,
            field: col.field,
            header: col.header,
            headerDefault: col.headerDefault,
            width: col.width || '20%',
            sortable: col.sortable ?? true,
            order: col.order || 0,
          };
        });

        setColumnMeta(mappedColumns);
      }
      setColumnsLoaded(true);
    });
  };

  // Built fresh on every render so render closures capture the current expansion state
  const columnConfig: ColumnConfig<ExtendedReservationListPreviewOutDTO>[] = [...columnMeta]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(meta => ({ ...meta, render: getColumnRenderer(meta) }));

  const canEdit = (data: ExtendedReservationListPreviewOutDTO) => {
    // jsem vlastnik
    if (data.customerId == loggedUser.companyId) return true;

    if (data.customerCode !== 'MAGNA-CARTECH') return true;

    // neni zadany datum
    if (isNullOrUndefined(data.timeslot)) return true;

    // neni mene jak 60 minut do zacatku akce
    if (
      data.customerCode === 'MAGNA-CARTECH' &&
      (isNotNullOrUndefined(data.timeslot?.timezoneId) && isNullOrUndefined(data.timeslot?.utcStart)
        ? dayjsFromApiUtcInTz(data.timeslot.utcStart, data.timeslot.timezoneId).isAfter(dayjs().utc().add(60, 'minutes'))
        : dayjsFromApiPrague(data.timeslot?.since).isAfter(dayjs().add(60, 'minutes')))
    )
      return true;

    return false;
  };

  const loadDataApi = (filter: AllReservationsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<ExtendedReservationListPreviewOutDTO>) => void) => {
    getReservations(pageable, normalizeReservationFilter(filter), d => {
      onSuccess({
        data: d.data as ExtendedReservationListPreviewOutDTO[],
        total: d.total,
        page: d.page,
        size: d.size,
      });
    });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.applicationId', 'Application ID'),
      value: ['applicationId'],
      dataTestId: 'reservations-filter-applicationId',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.locality', 'Locality'),
      value: ['locationName'],
      dataTestId: 'reservations-filter-locality',
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr('reservations.since', 'Since'),
      value: ['timeslotDaySince'],
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr('reservations.till', 'Till'),
      value: ['timeslotDayTill'],
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.reservationName', 'Reservation name'),
      value: ['reservationName'],
      dataTestId: 'reservations-filter-reservationName',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('reservations.carrierName', 'Carrier name'),
      value: ['carrierIds'],
      options: carrierOptions,
      dataTestId: 'reservations-filter-carrierIds',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.referenceID', 'Reference'),
      value: ['referenceOrderId'],
      dataTestId: 'reservations-filter-referenceOrderId',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.driverName', 'Driver name'),
      value: ['driverName'],
      dataTestId: 'reservations-filter-driverName',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('reservations.carPlate', 'Car plate'),
      value: ['carPlate'],
      dataTestId: 'reservations-filter-carPlate',
    },
    ...(hasOwnedTimeslotModule
      ? [
          {
            filterType: FilterType.MultiSelect,
            placeholder: tr('InputLabel.labels', 'Labels'),
            value: ['labelIds'],
            options: labelOptions,
          } as FilterConfig,
        ]
      : []),
    ...(hasOwnedTimeslotModule && reservationFolders.length > 0
      ? [
          {
            filterType: FilterType.MultiSelect,
            placeholder: tr('reservations.folders', 'Složky'),
            value: ['folderIds'],
            options: reservationFolders.map(f => ({ label: f.name, value: f.id, active: true })),
          } as FilterConfig,
        ]
      : []),
    ...(hasOwnedTimeslotModule && timeslotStates && timeslotStates.length > 0
      ? [
          {
            filterType: FilterType.MultiSelect,
            placeholder: tr('reservations.states', 'States'),
            value: ['states'],
            options: timeslotStates.map(state => ({
              label: tr(`CreateEditWorkflowStateComponent.stateName${state}`, state),
              value: state,
              active: true,
            })),
          } as FilterConfig,
        ]
      : []),
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('reservations.actionType', 'Action Type'),
      value: ['reservationActionType'],
      options: Object.values(ReservationActionTypeEnum).map(val => ({
        label: tr(`ReservationActionTypeEnum.${val}`, val),
        value: val,
        active: true,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('reservations.customerName', 'Customer name'),
      value: ['customerIds'],
      options: customerOptions,
      dataTestId: 'reservations-filter-customerIds',
    },
    ...(hasOnBehalfOf
      ? [
          {
            filterType: FilterType.MultiSelect,
            placeholder: tr('reservations.onBehalfOf', 'On behalf of'),
            value: ['onBehalfOfIds'],
            options: onBehalfOfOptions,
            dataTestId: 'reservations-filter-onBehalfOf',
          } as FilterConfig,
        ]
      : []),
    {
      filterType: FilterType.Checkbox,
      placeholder: tr('AllReservation.onlyArchived', 'Archived'),
      value: ['isArchived'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('generic.attachmentName', 'Attachment name'),
      value: ['attachmentName'],
      dataTestId: 'reservations-filter-attachmentName',
    },
  ];

  const openEdit = (reservation: ExtendedReservationListPreviewOutDTO) => {
    setEditReservation(reservation);
    setEditSidebarVisible(true);
  };

  const renderMobileCard = (reservation: ExtendedReservationListPreviewOutDTO, checkbox?: JSX.Element | null) => {
    const sortedColumns = [...columnConfig].filter(c => !c.hidden).sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
      <div className="bg-white p-4 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 px-4 relative z-10">
        {checkbox && <div className="mb-2">{checkbox}</div>}
        <div className="flex flex-col gap-4">
          {sortedColumns.map((column, index) => (
            <div key={`${reservation.id}-${column.id}`} className={clsx('mobile-section', index !== 0 && 'pt-4', index >= 2 && 'border-t border-gray-100')}>
              {column.render(reservation)}
            </div>
          ))}
        </div>

        {canEdit(reservation) && (
          <div className="mt-4 flex">
            <Button
              onClick={() => openEdit(reservation)}
              className="!rounded-[4px] p-button-outlined !bg-white !border-gray-300 !text-gray-600 hover:!bg-gray-50 !shadow-[0px_4px_4px_rgba(254,80,0,0.15)]"
              label={tr('reservations.editTimeSlot', 'Edit time slot')}
            />
          </div>
        )}
      </div>
    );
  };

  // Beta SPLIT_VIEW: the v3 table with tabs, its columns built from the field catalogue.
  // Rebuilt on every render so the fields see the current expanded tags and labels.
  const reservationFields = buildReservationFields({
    tr,
    detailLink: row => navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(row.id)),
    isOwner: row => !!loggedUser && loggedUser.companyId === row.customerId,
    infoTags: { isExpanded: key => !!expandedFlagTags?.[key], toggle: toggleFlagTag, setAll: setAllFlagTagsExpanded },
    labels: { expanded: revealTabTexts, toggle: () => setRevealTabTexts(v => !v) },
    onAttachmentClick: handleFileClick,
  });
  const reservationViews: TableViewsConfig<ExtendedReservationListPreviewOutDTO> = {
    tableKey: TableKeyEnum.RESERVATION_LIST,
    catalogue: reservationFields,
    presets: RESERVATION_PRESETS,
    // New tabs get today's five cells in the order the company set for its reservation list.
    defaultColumns: reservationDefaultColumns([...columnMeta].sort((a, b) => (a.order || 0) - (b.order || 0)).map(column => column.id)),
    defaultSort: { sortField: 'applicationId', sortDirection: 'DESC' },
    // Stored as the tab's name when it is created, so it is translated once, in the user's language at that moment.
    firstViewName: tr('generic.all', 'All'),
    // Old saved filters become tabs once, on the first visit; best-effort and silent, as on the other lists.
    importLegacy: () =>
      new Promise(resolve =>
        getUserReservationFilters(
          data => resolve((data?.filters ?? []).map(saved => ({ name: saved.name, filter: (saved.data ?? {}) as Record<string, unknown> }))),
          null,
          { onError: () => resolve([]), onValidationFailed: () => resolve([]) },
        ),
      ),
    groups: RESERVATION_FIELD_GROUPS,
  };

  if (isNullOrUndefined(loggedUser)) return <></>;

  return (
    <>
      <div className="font-['DM Sans']">
        <BreadCrumbComponent items={[{ label: tr(`ReservationDetail.bcReservationList`, `Seznam rezervací`) }]} />
        <div className="flex items-center">
          <Heading title={tr(`reservations.reservations`, `Reservations`)} headingVariant={'h1'} />
          <SecuredArea id="reservation-list-create" visible={timeslotListRwFlag} label="New reservation button" description="Button that creates a new reservation">
            <RingilButton
              mode="link"
              label={tr(`reservations.newReservation`, `New reservation`)}
              icon={<i className="pi pi-plus" />}
              onClick={() => {
                setCreateTimeslotRequest({
                  applicationId: null,
                  companyLocationId: null,
                  preferredDate: '',
                  companyLocationCustomerId: null,
                  expeditionLocationId: null,
                  expeditionId: null,
                  timezoneId: null,
                });
              }}
            />
          </SecuredArea>
        </div>
      </div>

      {/* Beta SPLIT_VIEW: the same list on the compact v3 table. Both get one set of props. */}
      <LegacyVersionSwitch
        surface={BetaSurface.SPLIT_VIEW}
        labels={{
          toNew: tr('reservations.switchToNewTable', 'Switch to new table'),
          toLegacy: tr('reservations.switchToLegacyTable', 'Switch to legacy table'),
        }}
      >
        {showLegacy => {
          const tableProps = {
            filtersConf,
            loadDataFromApi: loadDataApi,
            filterName: 'reservations_list',
            getSavedFilters: getUserReservationFilters,
            saveFilters: saveUserReservationFilters,
            deleteSavedFilter: deleteUserFilter,
            renderMobileCard: isMobile ? renderMobileCard : undefined,
            normalizeFilter: normalizeReservationFilter,
            massActionConfig,
            exportConfig: {
              entityType: EntityTypeEnum.TIMESLOT,
              entityLabel: 'Reservations',
              mapFilter: (f: AllReservationsFilter): AllReservationsFilter => ({ ...f, referenceOrderId: null }),
              secured: reservationExportFlag,
            },
          };
          if (showLegacy) return <RingilDataTable ref={tableRef} columnConfig={columnConfig} {...tableProps} />;
          // Waits for the company column order: a first visit creates the "All" tab in it.
          if (!columnsLoaded) return null;
          return (
            <RingilDataTableV3
              ref={tableRef}
              views={reservationViews}
              enableReload
              infiniteScroll
              // The split layouts (profile menu → page layout) show the open reservation's detail beside or below the list.
              split={{
                renderDetail: (row, { onChanged }) => <ReservationDetail key={row.id} reservationId={row.id} embedded onChanged={onChanged} />,
              }}
              {...tableProps}
            />
          );
        }}
      </LegacyVersionSwitch>

      <ConfirmDialog
        visible={pendingMassDelete.length > 0}
        dialogTitle={tr('reservations.massDeleteHeading', 'Delete reservations')}
        dialogMessage={tr('reservations.massDeleteConfirm', 'Delete {{count}} reservations? This cannot be undone.', { count: pendingMassDelete.length })}
        onAcceptButtonText={tr('generic.delete', 'Delete')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
        loading={massActionBusy}
        onConfirm={confirmMassDelete}
        onCancel={() => setPendingMassDelete([])}
        dataTestId={'reservations-mass-delete-confirm'}
      />

      {createTimeslotRequest && (
        <CreateTimeslotSidebar
          onContinue={() => {
            tableRef.current?.reloadData();
          }}
          headerDisabled={false}
        />
      )}

      {editSidebarVisible && editReservation && (
        <EditReservationSlotSidebar
          applicationId={editReservation.applicationId || ''}
          companyLocationCustomerId={editReservation.customerId || ''}
          reservationId={editReservation.id}
          onRefresh={() => {
            tableRef.current?.reloadData();
            setEditSidebarVisible(false);
            setEditReservation(null);
          }}
          expeditionLocationId={editReservation.locationId || ''}
        />
      )}
    </>
  );
};
